import { Api } from "services/api/api.service";

import {
  CreateGigPayload,
  buildCreateGigPayload,
  getCreateGigItems,
} from "../logic/gigForm.payload.mapper";
import type { FormData, RecruitFormData, SelectedWorker } from "./gigFormModal.types";

type AlertDispatch = (action: {
  type: "SHOW_ALERT";
  payload: {
    icon: string;
    title: string;
    message: string;
  };
}) => void;

type TranslationFn = (key: string) => string;

type BaseSubmitInput = {
  alertDispatch: AlertDispatch;
  companyId: number;
  formData: FormData;
  recruitFormData: RecruitFormData;
  setIsLoading: (value: boolean) => void;
  t: TranslationFn;
};

type CreateGigInput = BaseSubmitInput & {
  isLoadingManagedExternally?: boolean;
  onCreated?: () => void;
  parentGigId?: number;
  selectedWorkers: SelectedWorker[];
  setShowGigFormModal: (value: boolean) => void;
};

/**
 * Creates one or multiple gigs from the current form state and selected workers.
 */
export const submitCreateGig = async (input: CreateGigInput): Promise<boolean> => {
  if (!input.isLoadingManagedExternally) {
    input.setIsLoading(true);
  }
  const items = getCreateGigItems(input.formData.type, input.selectedWorkers);

  try {
    await Promise.all(
      items.map((worker) => {
        const payload: CreateGigPayload & {
          customerProduct?: string;
        } = {
          ...buildCreateGigPayload(
            input.formData,
            input.recruitFormData,
            input.parentGigId,
            worker,
          ),
          customerProduct: worker?.selectedCustomerProduct,
        };

        return Api().company.gig.createGig(input.companyId, payload);
      }),
    );

    input.alertDispatch({
      type: "SHOW_ALERT",
      payload: {
        icon: "",
        title: input.t("CreateShift.Created"),
        message: input.t("CreateShift.YourGigsHaveBeenCreated"),
      },
    });
    input.setShowGigFormModal(false);
    input.onCreated?.();
    return true;
  } catch (_error: unknown) {
    input.alertDispatch({
      type: "SHOW_ALERT",
      payload: {
        icon: "alert",
        title: input.t("Alerts.OhNo"),
        message: input.t("Alerts.SomethingWentWrong"),
      },
    });
    return false;
  } finally {
    if (!input.isLoadingManagedExternally) {
      input.setIsLoading(false);
    }
  }
};

/**
 * Creates a parent gig and then creates child gigs linked to that parent.
 */
export const submitCreateParentGig = async (
  input: Omit<CreateGigInput, "parentGigId">,
): Promise<void> => {
  input.setIsLoading(true);

  try {
    const response = await Api().company.gig.createParentGig(input.companyId);
    await submitCreateGig({
      ...input,
      isLoadingManagedExternally: true,
      parentGigId: response.data,
    });
  } catch (_error: unknown) {
    input.alertDispatch({
      type: "SHOW_ALERT",
      payload: {
        icon: "alert",
        title: input.t("Alerts.OhNo"),
        message: input.t("Alerts.SomethingWentWrong"),
      },
    });
  } finally {
    input.setIsLoading(false);
  }
};
